import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency, formatDate } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Sparkles, Trash2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { PgcAccountPicker } from '@/components/PgcAccountPicker';

export default function Expenses() {
  const { t, lang } = useI18n();
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [e, a, s] = await Promise.all([
      api.get('/expenses'),
      api.get('/pgc-accounts'),
      api.get('/suppliers'),
    ]);
    setExpenses(e.data); setAccounts(a.data); setSuppliers(s.data);
  }, []);
  useEffect(() => { load(); }, [load, load]);

  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_expenses')}</h1>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="expenses-new-button"><Plus className="h-4 w-4 mr-1" /> {t('new_expense')}</Button>
      </div>
      {expenses.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Receipt className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Sin gastos' : 'Sem despesas'}</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('date')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('description')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('pgc_account')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('base')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('iva')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('total')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(e => (
                    <tr key={e.id} className="border-t cursor-pointer hover:bg-[hsl(var(--muted))]/40" onClick={() => { setEditing(e); setOpen(true); }} data-testid={`expense-row-${e.id}`}>
                      <td className="px-3 py-2 text-xs">{formatDate(e.fecha, lang)}</td>
                      <td className="px-3 py-2 max-w-xs truncate">{e.descripcion}<div className="text-xs text-[hsl(var(--muted-foreground))]">{e.proveedor_nombre || ''}</div></td>
                      <td className="px-3 py-2"><Badge variant="outline" className="font-mono text-xs">{e.pgc_codigo}</Badge></td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono">{formatCurrency(e.base, lang)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono text-xs">{e.iva_pct}%</td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono font-semibold">{formatCurrency(e.total, lang)}</td>
                      <td className="px-3 py-2"><Badge variant={e.estado === 'pagado' ? 'default' : 'outline'}>{e.estado === 'pagado' ? t('payment_paid') : t('payment_pending')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <ExpenseDialog open={open} onOpenChange={setOpen} editing={editing} accounts={accounts} suppliers={suppliers} t={t} lang={lang} onSaved={load} />
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, editing, accounts, suppliers, t, lang, onSaved }) {
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '', proveedor_id: '', proveedor_nombre: '', pgc_codigo: '629',
    base: 0, iva_pct: 21, metodo_pago: 'tarjeta', estado: 'pendiente',
    factura_numero: '', notas: '',
  });
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        ...editing,
        fecha: (editing.fecha || '').slice(0, 10),
        proveedor_id: editing.proveedor_id || '',
        factura_numero: editing.factura_numero || '',
        notas: editing.notas || '',
      });
    } else {
      setForm({
        fecha: new Date().toISOString().slice(0, 10),
        descripcion: '', proveedor_id: '', proveedor_nombre: '', pgc_codigo: '629',
        base: 0, iva_pct: 21, metodo_pago: 'tarjeta', estado: 'pendiente',
        factura_numero: '', notas: '',
      });
    }
    setAiResult(null);
  }, [editing, open]);

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const r = await api.post('/ai/suggest-pgc', { descripcion: form.descripcion, lang });
      setAiResult(r.data);
      if (r.data.cuenta_pgc) setForm({ ...form, pgc_codigo: r.data.cuenta_pgc });
    } catch (e) { toast.error('AI error'); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    const payload = {
      ...form,
      fecha: form.fecha ? new Date(form.fecha).toISOString() : null,
      proveedor_id: form.proveedor_id || null,
    };
    if (editing) await api.put(`/expenses/${editing.id}`, payload);
    else await api.post('/expenses', payload);
    toast.success('OK'); onOpenChange(false); onSaved();
  };
  const remove = async () => { await api.delete(`/expenses/${editing.id}`); onOpenChange(false); onSaved(); };

  const total = (form.base || 0) * (1 + (form.iva_pct || 0) / 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="expense-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : t('new_expense')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('date')}</Label><Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} /></div>
            <div><Label>{t('invoice_number')}</Label><Input value={form.factura_numero} onChange={e => setForm({ ...form, factura_numero: e.target.value })} /></div>
          </div>
          <div>
            <Label>{t('description')}</Label>
            <div className="flex gap-2">
              <Input value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder={lang === 'es' ? 'Ej: Factura electricidad Iberdrola' : 'Ex: Fatura eletricidade'} data-testid="expense-description-input" />
              <Button type="button" size="sm" variant="outline" onClick={aiSuggest} disabled={!form.descripcion || aiLoading} data-testid="expense-ai-suggest-button">
                <Sparkles className="h-3.5 w-3.5 mr-1" /> {aiLoading ? '...' : 'IA'}
              </Button>
            </div>
          </div>
          {aiResult && (
            <Card className="border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5">
              <CardContent className="p-2.5 text-xs space-y-0.5">
                <div className="flex items-center gap-2 font-semibold text-[hsl(var(--primary))]"><Sparkles className="h-3.5 w-3.5" /> {t('ai_suggest_pgc')}</div>
                <div><span className="font-mono">{aiResult.cuenta_pgc}</span> - {aiResult.nombre_cuenta}</div>
                {aiResult.confianza && <div>{t('ai_confidence')}: {Math.round((aiResult.confianza || 0) * 100)}%</div>}
                {aiResult.explicacion && <div className="italic">{aiResult.explicacion}</div>}
              </CardContent>
            </Card>
          )}
          <div>
            <Label>{t('pgc_account')}</Label>
            <PgcAccountPicker accounts={accounts} value={form.pgc_codigo} onChange={v => setForm({ ...form, pgc_codigo: v })} testId="expense-pgc-picker" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>{t('base')}</Label><Input type="number" step="0.01" value={form.base} onChange={e => setForm({ ...form, base: Number(e.target.value) })} data-testid="expense-base-input" /></div>
            <div><Label>{t('iva_pct')}</Label>
              <Select value={String(form.iva_pct)} onValueChange={v => setForm({ ...form, iva_pct: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">0%</SelectItem><SelectItem value="4">4%</SelectItem><SelectItem value="10">10%</SelectItem><SelectItem value="21">21%</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t('total')}</Label><Input value={formatCurrency(total, lang)} readOnly className="font-mono" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('supplier_name')}</Label>
              <Select value={form.proveedor_id || 'none'} onValueChange={v => {
                if (v === 'none') { setForm({ ...form, proveedor_id: '', proveedor_nombre: '' }); return; }
                const s = suppliers.find(ss => ss.id === v);
                setForm({ ...form, proveedor_id: v, proveedor_nombre: s?.nombre || '' });
              }}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t('payment_method')}</Label>
              <Select value={form.metodo_pago} onValueChange={v => setForm({ ...form, metodo_pago: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">{t('cash')}</SelectItem>
                  <SelectItem value="tarjeta">{t('card')}</SelectItem>
                  <SelectItem value="transferencia">{t('transfer')}</SelectItem>
                  <SelectItem value="otro">{t('other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>{t('status')}</Label>
            <Select value={form.estado} onValueChange={v => setForm({ ...form, estado: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="pendiente">{t('payment_pending')}</SelectItem><SelectItem value="pagado">{t('payment_paid')}</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="destructive" onClick={remove}><Trash2 className="h-4 w-4" /></Button>}
          <Button onClick={save} disabled={!form.descripcion || !form.pgc_codigo} data-testid="expense-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
